import {
    LimitedCloudVisionResponse,
    VerticesEntityOrNormalizedVerticesEntity,
} from '../../types/cloudVision';

export type Vertices = VerticesEntityOrNormalizedVerticesEntity[];

type Slab = {
    top: number;
    bottom: number;
};

type Relation =
    | 'above'
    | 'bellow'
    | 'intersectTop'
    | 'intersectBottom'
    | 'intersectBoth'
    | 'contains'
    | 'fail';

export function analyze(
    data: LimitedCloudVisionResponse,
    availableHeight: number,
    _availableWidth: number,
    naturalHeight: number,
    naturalWidth: number,
    anotate = false
) {
    const naturalArea = naturalHeight * naturalWidth;
    const pixelsAvailableSpace = availableHeight;
    const availableTop = naturalHeight / 2 - pixelsAvailableSpace / 2;
    const availableBottom = availableTop + pixelsAvailableSpace;
    const info = anotate ? console.info : () => {};

    const doNotIntersect: Vertices[] = [];

    let adjustedSlab = {
        top: availableTop,
        bottom: availableBottom,
    };

    let afterLogoTop = 0;

    let frontAndCenterLogo = undefined;
    let frontAndCenterLogoSize = 0;

    // Logos (text)
    if (
        data.fullTextAnnotation &&
        data.fullTextAnnotation.pages &&
        data.fullTextAnnotation.pages.length
    ) {
        const firstPage = data.fullTextAnnotation.pages[0];
        if (firstPage && firstPage.blocks) {
            firstPage.blocks.forEach((block) => {
                if (
                    block.boundingBox.vertices &&
                    above(block.boundingBox.vertices, 80) &&
                    onCenterline(block.boundingBox.vertices, naturalWidth / 2)
                ) {
                    info('Trying to avoid topbar logo');
                    if (above(block.boundingBox.vertices, 50)) {
                        afterLogoTop = 50;
                    } else if (!afterLogoTop) {
                        afterLogoTop = 120;
                    }
                }
                // avoid the rest
                else {
                    // doNotIntersect.push(block.boundingBox.vertices);
                }
                const logoSize = block.boundingBox.vertices
                    ? (size(block.boundingBox.vertices) / naturalArea) * 100
                    : 0;

                if (logoSize > 1.5) {
                    if (
                        block.boundingBox.vertices &&
                        isLevel(block.boundingBox.vertices)
                    ) {
                        if (block.boundingBox.vertices) {
                            doNotIntersect.push(block.boundingBox.vertices);
                        }
                    }
                    if (
                        block.boundingBox.vertices &&
                        onCenterline(
                            block.boundingBox.vertices,
                            naturalWidth / 2
                        ) &&
                        onVerticalCenterline(
                            block.boundingBox.vertices,
                            naturalHeight / 2
                        )
                    ) {
                        if (logoSize > frontAndCenterLogoSize) {
                            frontAndCenterLogo = block.boundingBox.vertices;
                        }
                    }
                }
            });
        }
    }

    let facesTop: number | undefined;
    let facesBottom: number | undefined;

    const faces = data.faceAnnotations;

    if (faces && faces.length) {
        faces.forEach((face) => {
            if (face.fdBoundingPoly.vertices) {
                doNotIntersect.push(face.fdBoundingPoly.vertices);
            }
            if (face.boundingPoly.vertices) {
                const { top, bottom } = minmax(face.boundingPoly.vertices);
                if (!facesTop || top < facesTop) {
                    facesTop = top;
                }
                if (!facesBottom || bottom > facesBottom) {
                    facesBottom = bottom;
                }
            }
        });

        if (facesTop && facesBottom) {
            const faceDistance = facesBottom - facesTop;
            if (faceDistance < pixelsAvailableSpace) {
                const remainder = pixelsAvailableSpace - faceDistance;
                const shift = Math.floor(remainder / 4);
                adjustedSlab = validSlab(
                    facesTop - shift,
                    facesTop - shift + pixelsAvailableSpace,
                    afterLogoTop,
                    naturalHeight
                );
                info('Prioritizing all faces in the image');
            }
        } else {
            // one big face
            if (faces.length === 1) {
                const onlyFace = faces[0];
                if (onlyFace.boundingPoly.vertices) {
                    let { top: faceTop, bottom: faceBottom } = minmax(
                        onlyFace.boundingPoly.vertices
                    );

                    if (faceBottom - faceTop > pixelsAvailableSpace) {
                        // center on face
                        const midpoint =
                            onlyFace.landmarks &&
                            onlyFace.landmarks.find(
                                (landmark) =>
                                    landmark.type === 'MIDPOINT_BETWEEN_EYES'
                            );

                        if (midpoint) {
                            adjustedSlab = validSlab(
                                (midpoint.position.y || 0) -
                                    pixelsAvailableSpace / 2,
                                (midpoint.position.y || 0) +
                                    pixelsAvailableSpace / 2,
                                afterLogoTop,
                                naturalHeight
                            );
                            info(
                                'Prioritizing midpoint of a face that is to large'
                            );
                        }
                    } else {
                        adjustedSlab = validSlab(
                            faceTop,
                            faceTop + pixelsAvailableSpace,
                            afterLogoTop,
                            naturalHeight
                        );
                        info('Prioritizing one large face');
                    }
                }
            } else {
                const vertices = faces
                    .map((face) => face.boundingPoly.vertices || undefined)
                    .filter((vertice) => vertice !== undefined) as Vertices[];

                // adjustedSlab = prioritizeSize(vertices, pixelsAvailableSpace);
                if (vertices) {
                    adjustedSlab = prioritizeGrouping(
                        vertices,
                        pixelsAvailableSpace,
                        afterLogoTop,
                        naturalHeight
                    );
                    info(
                        'Prioritizing the largest grouping of faces that fits the available space'
                    );
                }

                // intersect
            }
        }
    } else if (frontAndCenterLogo) {
        let { top, bottom } = minmax(frontAndCenterLogo);

        const centerPoint = (bottom + top) / 2;

        adjustedSlab = validSlab(
            centerPoint - pixelsAvailableSpace / 2,
            centerPoint + pixelsAvailableSpace / 2,
            afterLogoTop,
            naturalHeight
        );
        info('Focusing on a large center logo');
    } else {
        if (
            data.localizedObjectAnnotations &&
            data.localizedObjectAnnotations.length > 0
        ) {
            const localizedObjects = data.localizedObjectAnnotations;
            const people = localizedObjects.filter(
                (object) => object.name === 'Person'
            );

            if (people.length > 0) {
                // no faces, but people

                let peopleTop: number | undefined;

                let peopleBottom: number | undefined;

                people.forEach((person) => {
                    // doNotIntersect.push(person.boundingPoly.normalizedVertices);
                    if (person.boundingPoly.normalizedVertices) {
                        const { top, bottom } = minmax(
                            person.boundingPoly.normalizedVertices,
                            naturalHeight
                        );
                        if (!peopleTop || top < peopleTop) {
                            peopleTop = top;
                        }
                        if (!peopleBottom || bottom > peopleBottom) {
                            peopleBottom = bottom;
                        }
                    }
                });
                // do nothing

                if (peopleTop && peopleBottom) {
                    const peopleDistance = peopleBottom - peopleTop;
                    if (peopleDistance < pixelsAvailableSpace) {
                        const remainder = pixelsAvailableSpace - peopleDistance;
                        const shift = Math.floor(remainder / 4);
                        adjustedSlab = validSlab(
                            peopleTop - shift,
                            peopleTop - shift + pixelsAvailableSpace,
                            afterLogoTop,
                            naturalHeight
                        );
                    } else {
                        adjustedSlab = validSlab(
                            peopleTop,
                            peopleTop + pixelsAvailableSpace,
                            afterLogoTop,
                            naturalHeight
                        );
                    }
                    info('Prioritizing the people in the image');
                }
            } else if (data.localizedObjectAnnotations.length < 5) {
                const sorted = localizedObjects.sort((a, b) =>
                    bySize(
                        a.boundingPoly.normalizedVertices,
                        b.boundingPoly.normalizedVertices
                    )
                );
                const biggest = sorted[0];

                if (biggest.boundingPoly.normalizedVertices) {
                    let { top: innerTop, bottom: innerBottom } = minmax(
                        biggest.boundingPoly.normalizedVertices
                    );

                    const centerPoint =
                        ((innerBottom + innerTop) / 2) * naturalHeight;

                    adjustedSlab = validSlab(
                        centerPoint - pixelsAvailableSpace / 2,
                        centerPoint + pixelsAvailableSpace / 2,
                        afterLogoTop,
                        naturalHeight
                    );
                    info('Prioritizing the largest object in the image');
                }
            }
        }
    }

    adjustedSlab = adjustToAvoidIntersect(
        doNotIntersect,
        pixelsAvailableSpace,
        adjustedSlab,
        afterLogoTop,
        naturalHeight
    );

    return { ...adjustedSlab, debug: doNotIntersect };
}

function size(vertices: Vertices): number {
    const x1 = vertices[0].x || 0;
    const y1 = vertices[0].y || 0;
    const x2 = vertices[1].x || 0;
    const y2 = vertices[1].y || 0;
    const x3 = vertices[2].x || 0;
    const y3 = vertices[2].y || 0;
    const x4 = vertices[3].x || 0;
    const y4 = vertices[3].y || 0;
    const result = Math.abs(
        (x1 * y2 -
            y1 * x2 +
            (x2 * y3 - y2 * x3) +
            (x3 * y4 - y3 * x4) +
            (x4 * y1 - y4 * x1)) /
            2
    );

    return result;
}

function minmax(vertices: Vertices, normalizedHeight = 1): Slab {
    let top = vertices[0].y || 0;
    let bottom = vertices[0].y || 0;
    vertices.forEach(({ y = 0 }) => {
        if (y < top) {
            top = y;
        }
        if (y > bottom) {
            bottom = y;
        }
    });
    return { top: top * normalizedHeight, bottom: bottom * normalizedHeight };
}

function center(vertices: Vertices) {
    const { top, bottom } = minmax(vertices);
    return (bottom + top) / 2;
}

function shorten(vertices: Vertices[], available: number): Slab {
    if (vertices.length > 1) {
        const subset = vertices.slice(0, vertices.length - 1);
        const { top, bottom } = total(subset);
        if (bottom - top < available) {
            return { top, bottom };
        } else {
            return shorten(subset, available);
        }
    }

    return minmax(vertices[0]);
}

export function prioritizeSize(vertices: Vertices[], available: number): Slab {
    const sorted = vertices.sort(bySize);
    let { top: innerTop } = shorten(sorted, available);

    return {
        top: innerTop,
        bottom: innerTop + available,
    };
}

export function prioritizeGrouping(
    vertices: Vertices[],
    available: number,
    start: number,
    total: number
): Slab {
    const sorted = vertices.sort(bySize);
    let { top: innerTop } = shortenByDistance(sorted, available);

    return validSlab(innerTop, innerTop + available, start, total);
}

function shortenByDistance(vertices: Vertices[], available: number): Slab {
    if (vertices.length > 1) {
        const mean =
            vertices.reduce((accumulator, currentValue) => {
                return accumulator + center(currentValue);
            }, 0) / vertices.length;

        const sorted = vertices.sort((a, b) => byDistance(a, b, mean));
        const subset = sorted.slice(0, vertices.length - 1);
        const { top, bottom } = total(subset);
        if (bottom - top < available) {
            return { top, bottom };
        } else {
            return shortenByDistance(subset, available);
        }
    }

    return minmax(vertices[0]);
}

function byDistance(a: Vertices, b: Vertices, to: number) {
    const aLength = Math.abs(center(a) - to);
    const bLength = Math.abs(center(b) - to);
    if (aLength < bLength) {
        return -1;
    }
    if (aLength > bLength) {
        return 1;
    }

    return 0;
}

function above(vertices: Vertices, line: number) {
    return !vertices.find((vertex) => vertex.y && vertex.y > line) || false;
}

function onCenterline(vertices: Vertices, line: number) {
    const left = vertices.filter((vertex) => (vertex.x || 0) < line);
    return left.length === 2;
}

function onVerticalCenterline(vertices: Vertices, line: number) {
    const left = vertices.filter((vertex) => (vertex.y || 0) < line);
    return left.length === 2;
}

function total(vertices: Vertices[]): Slab {
    let totalTop: number;
    let totalBottom: number;
    const first = vertices[0];
    let { top, bottom } = minmax(first);
    totalTop = top;
    totalBottom = bottom;

    vertices.forEach((vertice) => {
        const { top, bottom } = minmax(vertice);
        if (top < totalTop) {
            totalTop = top;
        }
        if (bottom > totalBottom) {
            totalBottom = bottom;
        }
    });
    return { top: totalTop, bottom: totalBottom };
}

function intersect(a: Slab, b: Slab): Relation {
    if (a.bottom < b.top) {
        return 'above';
    } else if (a.top > b.bottom) {
        return 'bellow';
    } else if (a.top > b.top && a.bottom < b.bottom) {
        return 'contains';
    } else if (a.top < b.top && a.bottom > b.bottom) {
        return 'intersectBoth';
    } else if (a.top < b.top && a.bottom < b.bottom) {
        return 'intersectTop';
    } else if (a.top > b.top && a.bottom > b.bottom) {
        return 'intersectBottom';
    }
    return 'fail';
}

function distance(a: Slab, b: Slab): number {
    return Math.abs(a.bottom - a.top - (b.bottom - b.top));
}

function adjustToAvoidIntersect(
    vertices: Vertices[],
    available: number,
    candidate: Slab,
    start: number,
    total: number
): Slab {
    let bestCandidate = candidate;

    const problems = vertices.filter((vertice) => {
        const i = intersect(minmax(vertice), candidate);
        if (i === 'intersectTop' || i === 'intersectBottom') {
            return true;
        }
        return false;
    });

    let problemsInBestCandidate = problems.length;

    const solutions = problems.map((vertice) => {
        const problemSlab = minmax(vertice);
        const i = intersect(problemSlab, candidate);
        if (i === 'intersectTop') {
            const t = validSlab(
                problemSlab.bottom + 0.1 + available,
                problemSlab.bottom + 0.1,
                start,
                total
            );

            const td = distance(t, candidate);

            const b = validSlab(
                problemSlab.top - 0.1 - available,
                problemSlab.top - 0.1,
                start,
                total
            );

            const bd = distance(b, candidate);

            return td < bd ? t : b;
        } else if (i === 'intersectBottom') {
            const t = validSlab(
                problemSlab.bottom + 0.1 + available,
                problemSlab.bottom + 0.1,
                start,
                total
            );

            const td = distance(t, candidate);

            const b = validSlab(
                problemSlab.top - 0.1 - available,
                problemSlab.top - 0.1,
                start,
                total
            );

            const bd = distance(b, candidate);

            return td < bd ? t : b;
        }
        return candidate;
    });

    solutions.forEach((slab) => {
        const problemsInThisCandidate = evaluateSolution(vertices, slab);
        const features = featuresInSolution(vertices, slab);
        if (problemsInThisCandidate < problemsInBestCandidate && features > 0) {
            bestCandidate = slab;
            problemsInBestCandidate = problemsInThisCandidate;
        }
    });

    return bestCandidate;
}

function validSlab(top: number, bottom: number, start: number, total: number) {
    if (bottom - top > total) {
        return { top: 0, bottom: total };
    } else if (top < start && bottom - top < total - start) {
        return { top: start, bottom: bottom - top + start };
    } else if (top < 0) {
        return { top: 0, bottom: bottom - top };
    } else if (bottom > total) {
        return { top: top - (bottom - total), bottom: total };
    } else {
        return { top, bottom };
    }
}

function evaluateSolution(vertices: Vertices[], candidate: Slab): number {
    const problems = vertices.filter((vertice) => {
        const i = intersect(minmax(vertice), candidate);
        if (i === 'intersectTop' || i === 'intersectBottom') {
            return true;
        }
        return false;
    });

    return problems.length;
}

function featuresInSolution(vertices: Vertices[], candidate: Slab): number {
    const features = vertices.filter((vertice) => {
        const i = intersect(minmax(vertice), candidate);
        if (i === 'intersectBoth' || i === 'contains') {
            return true;
        }
        return false;
    });

    return features.length;
}

function intersectColor(a: Slab, b: Slab) {
    const i = intersect(a, b);

    switch (i) {
        case 'above':
        case 'bellow':
            return 'grey';
        case 'contains':
            return 'green';
        case 'intersectBoth':
        case 'intersectBottom':
        case 'intersectTop':
            return 'red';
        default:
            return 'black';
    }
}

function isLevel(rect1: Vertices) {
    const delta_x = (rect1[0].x || 0) - (rect1[1].x || 0);
    const delta_y = (rect1[0].y || 0) - (rect1[1].y || 0);
    const theta_radians = Math.atan2(delta_y, delta_x);
    const theta = theta_radians * (180 / Math.PI);
    const positive = Math.abs(theta);
    const diff = 1;
    if (
        (positive > -diff && positive < diff) ||
        (positive > 90 - diff && positive < 90 + diff) ||
        (positive > 180 - diff && positive < 180 + diff)
    ) {
        return true;
    }
    return false;
}

function bySize(a?: Vertices, b?: Vertices) {
    const aSize = a ? size(a) : 0;
    const bSize = b ? size(b) : 0;
    if (aSize < bSize) {
        return 1;
    }
    if (aSize > bSize) {
        return -1;
    }

    return 0;
}

export function annotateImage(
    img: HTMLImageElement,
    offset: Slab,
    intrinsicWidth: number,
    intrinsicScale2: number,
    doNotIntersect: Vertices[] = []
) {
    const parent = img.parentNode as HTMLElement;
    const naturalHeight = img.naturalHeight;
    const naturalWidth = img.naturalWidth;

    // const intrinsicWidth = 960;

    const availableHeight = parent.clientHeight;
    const availableWidth = parent.clientWidth;

    const scale = availableWidth / naturalWidth;
    const intrinsicScale = availableWidth / intrinsicWidth;

    const { left: viewportLeft, top: viewportTop } =
        parent.getBoundingClientRect();
    const container = document.body;
    const fullWidth = container.clientWidth;
    const fullHeight = container.clientHeight;

    let canvas: HTMLCanvasElement;
    const existing = parent.getElementsByTagName('canvas');
    if (existing.length) {
        canvas = existing[0];
    } else {
        canvas = document.createElement('canvas');
    }

    canvas.style.position = 'absolute';
    canvas.style.top = `-${viewportTop}px`;
    canvas.style.left = `-${viewportLeft}px`;
    canvas.height = fullHeight;
    canvas.width = fullWidth;

    parent.appendChild(canvas);

    if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const originalAlpha = ctx.globalAlpha;
            ctx.globalAlpha = 0.4;
            ctx.filter = 'grayscale(100%)';
            ctx.drawImage(
                img,
                viewportLeft,
                viewportTop - offset.top * intrinsicScale2,
                naturalWidth * scale,
                naturalHeight * scale
            );
            ctx.filter = 'none';
            ctx.globalAlpha = originalAlpha;
            ctx.globalCompositeOperation = 'destination-out';

            ctx.rect(
                viewportLeft,
                viewportTop,
                availableWidth,
                availableHeight
            );

            ctx.fill();
            ctx.globalCompositeOperation = 'source-over';

            doNotIntersect.forEach((vertices) => {
                const transformedVertices = transformVertices(
                    vertices,
                    intrinsicScale,
                    viewportLeft,
                    viewportTop - offset.top * intrinsicScale2
                );

                drawBox(
                    ctx,
                    transformedVertices,
                    2,
                    intersectColor(minmax(vertices), {
                        top: offset.top,
                        bottom: offset.bottom,
                    })
                );
            });
        }
    }
}
export function clearAnnotation(img: HTMLImageElement) {
    const parent = img.parentNode as HTMLElement;

    let canvas: HTMLCanvasElement;
    const existing = parent.getElementsByTagName('canvas');
    if (existing.length) {
        canvas = existing[0];
        canvas.remove();
    }
}

function transformCoordinates(
    coordinate: VerticesEntityOrNormalizedVerticesEntity,
    scale: number,
    offsetX: number,
    offsetY: number
) {
    return {
        x: (coordinate.x || 0) * scale + offsetX,
        y: (coordinate.y || 0) * scale + offsetY,
    };
}

function transformVertices(
    vertices: Vertices,
    scale: number,
    offsetX: number,
    offsetY: number
) {
    return vertices.map((coordinate) =>
        transformCoordinates(coordinate, scale, offsetX, offsetY)
    );
}

function drawBox(
    ctx: CanvasRenderingContext2D,
    vertices: Vertices,
    lineWidth = 4,
    color = 'black',
    dashed = false
) {
    ctx.beginPath();
    if (dashed) {
        ctx.setLineDash([5, 15]);
    }
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = color;
    ctx.moveTo(vertices[0].x || 0, vertices[0].y || 0);
    ctx.lineTo(vertices[1].x || 0, vertices[1].y || 0);
    ctx.lineTo(vertices[2].x || 0, vertices[2].y || 0);
    ctx.lineTo(vertices[3].x || 0, vertices[3].y || 0);
    ctx.lineTo(vertices[0].x || 0, vertices[0].y || 0);
    ctx.stroke();
    ctx.closePath();
    if (dashed) {
        ctx.setLineDash([]);
    }
}
